import './Profile.scss';
import '../Tabs.scss';

import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { Loader, Tab, Tabs } from '@trackman/web-shared-components';
import { ProfessionalLevel } from 'types/certificates';
import { ProfilePicture } from 'components/ProfilePicture/ProfilePicture';
import { ProfileTabsRelativeRoutes } from 'Routes';
import { ReactComponent as StarIcon } from 'images/icons/Star.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { getUsername } from 'utilities/userInfo';
import { useGetProfile } from 'queryHooks/Profile/useGetProfile';
import { useTranslation } from 'react-i18next';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { Outlet, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import { useTabsStore } from 'stores/TabsStore';
import { useGetUserProfessionalStatus } from 'queryHooks/Profile/useGetProfessionalStatus';
import { useGetUserCertificates } from 'queryHooks/Profile/useGetUserCertificates';
import defaultCertificateBadge from 'images/Certification/DefaultCertificateBadge.svg';
import { useGetUserBillingInfo } from 'queryHooks/Profile/Billing/useGetBillingInfo';

export const Profile = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();
  const { data: userProfessionalStatus, isPending: isUserProfessionalStatusPending } = useGetUserProfessionalStatus();
  const { data: userCertificates, isPending: isUserCertificatesPending } = useGetUserCertificates();
  const { data: billingInfo, isPending: isBillingInfoPending } = useGetUserBillingInfo();
  const isPending = isProfileDataPending || isUserCertificatesPending || isUserProfessionalStatusPending || isBillingInfoPending;

  useSetPageTitle({ title: t('ProfileSettings', { ns: 'general' }) });

  const { activeTabIndex } = useTabsStore();

  const getProfessionalStatus = (): string | undefined => {
    if (!userProfessionalStatus) {
      return undefined;
    }

    if (userProfessionalStatus['tmu-Master']) {
      return ProfessionalLevel.Master;
    }

    if (userProfessionalStatus['tmu-Level2']) {
      return ProfessionalLevel.Level2;
    }

    if (userProfessionalStatus['tmu-Level1']) {
      return ProfessionalLevel.Level1;
    }

    return undefined;
  };

  const getProfessionalStatusIconClass = (iconIndex: number) => {
    const baseClass = 'professional-status-icon';
    const status = getProfessionalStatus();
    if (status === undefined) {
      return baseClass;
    }

    const transformedProfessionalStatus: number = status === 'Master' ? 3 : Number(status);
    return iconIndex <= transformedProfessionalStatus ? `${baseClass} ${baseClass}--earned` : baseClass;
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{t('ProfileSettings', { ns: 'general' })}</h1>
        <span className='page-subtitle'>{t('CheckEditYourProfile', { ns: 'editProfile' })}</span>
      </div>
      {!isPending ? (
        <>
          <div className='profile-header'>
            <ProfilePicture />
            <div className='profile-data'>
              <div className='profile-data-section'>
                <h2>{getUsername(profileData?.firstName ?? '', profileData?.lastName ?? '')}</h2>
                <span className='profile-data-section--email'>{profileData?.email}</span>
              </div>

              <div className='profile-data-section'>
                <div className='profile-data-section--header'>
                  <span className='profile-data-section--title'>
                    {t('ProfessionalLevel', { ns: 'editProfile', level: getProfessionalStatus() })}
                  </span>
                  <Tooltip
                    className='profile-data-section--tooltip'
                    title={t('CompleteCertificationTestsToLevelUp', { ns: 'editProfile' })}
                    placement='bottom'
                  >
                    <InfoIcon />
                  </Tooltip>
                </div>
                <div className='professional-status'>
                  <StarIcon className={getProfessionalStatusIconClass(1)} />
                  <StarIcon className={getProfessionalStatusIconClass(2)} />
                  <StarIcon className={getProfessionalStatusIconClass(3)} />
                </div>
              </div>

              <div className='profile-data-section'>
                <span className='profile-data-section--title'>{t('CertificationsEarned', { ns: 'editProfile' })}</span>
                {userCertificates && userCertificates.length > 0 && (
                  <div className='user-certificate-badges'>
                    {userCertificates.map((c) => (
                      <Tooltip title={c.title}>
                        <img
                          className={
                            c.isCompleted ? 'user-certificate-badge' : 'user-certificate-badge user-certificate-badge--uncompleted'
                          }
                          src={c.badgeUrl ? c.badgeUrl : defaultCertificateBadge}
                        />
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <>
            <Box className='tabs-container'>
              <Tabs scrollButtons='auto' value={activeTabIndex} variant='scrollable' indicatorColor='primary' allowScrollButtonsMobile>
                <Tab
                  label={t('PlayerInformation', { ns: 'editProfile' })}
                  onClick={() => navigate(ProfileTabsRelativeRoutes.PlayerInformation)}
                />
                <Tab
                  label={t('PlayerSettings', { ns: 'editProfile' })}
                  onClick={() => navigate(ProfileTabsRelativeRoutes.PlayerSettings)}
                />
                <Tab
                  label={
                    typeof billingInfo === 'string' || billingInfo === undefined
                      ? ''
                      : t('BillingInformation', { ns: 'billingInformation' })
                  }
                  onClick={() => navigate(ProfileTabsRelativeRoutes.BillingAndPayment)}
                />
                <Tab
                  label={
                    typeof billingInfo === 'string' || billingInfo === undefined ? '' : t('Subscriptions', { ns: 'billingInformation' })
                  }
                  onClick={() => navigate(ProfileTabsRelativeRoutes.Subscriptions)}
                />
              </Tabs>
            </Box>
            <Outlet />
          </>
        </>
      ) : (
        <div className='new-loader-wrapper'>
          <Loader size='large' />
        </div>
      )}
    </>
  );
};

export default Profile;
